import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

export type RouteVideoRow = {
  slug: string;
  video_id: string;
  name: string | null;
  description: string | null;
  upload_date: string;
};

export type SiteSettingsMap = {
  title_suffix: string;
  home_show_tools: boolean;
  home_show_dashboard: boolean;
};

export type SiteContext = {
  settings: SiteSettingsMap;
  videos: Record<string, RouteVideoRow>;
};

const DEFAULTS: SiteSettingsMap = {
  title_suffix: "",
  home_show_tools: true,
  home_show_dashboard: true,
};

export const getSiteContext = createServerFn({ method: "GET" }).handler(async (): Promise<SiteContext> => {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const [s, v] = await Promise.all([
    supabaseAdmin.from("site_settings").select("key,value"),
    supabaseAdmin.from("route_videos").select("slug,video_id,name,description,upload_date"),
  ]);
  const settings: SiteSettingsMap = { ...DEFAULTS };
  for (const row of s.data ?? []) {
    if (row.key in settings) {
      (settings as Record<string, unknown>)[row.key] = row.value as unknown;
    }
  }
  const videos: Record<string, RouteVideoRow> = {};
  for (const row of (v.data ?? []) as RouteVideoRow[]) videos[row.slug] = row;
  return { settings, videos };
});

async function assertAdmin(ctx: { supabase: unknown; userId: string }) {
  const sb = ctx.supabase as { rpc: (fn: string, args: Record<string, unknown>) => Promise<{ data: boolean | null; error: { message: string } | null }> };
  const { data, error } = await sb.rpc("has_role", { _user_id: ctx.userId, _role: "admin" });
  if (error || !data) throw new Error("Forbidden");
}

const VideoInput = z.object({
  slug: z.string().min(1).max(120).regex(/^[a-z0-9/_\-.]+$/i),
  videoId: z.string().trim().max(200),
  name: z.string().trim().max(200).optional(),
  description: z.string().trim().max(1000).optional(),
});

function parseYouTubeId(input: string): string {
  const s = input.trim();
  if (!s) return "";
  const m =
    s.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/|v\/))([\w-]{6,})/i) ??
    s.match(/^([\w-]{6,})$/);
  return m ? m[1] : s;
}

export const saveRouteVideo = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => VideoInput.parse(d))
  .handler(async ({ data, context }) => {
    await assertAdmin(context as never);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const videoId = parseYouTubeId(data.videoId);
    if (!videoId) {
      const { error } = await supabaseAdmin.from("route_videos").delete().eq("slug", data.slug);
      if (error) throw new Error(error.message);
      return { ok: true, deleted: true };
    }
    const { error } = await supabaseAdmin
      .from("route_videos")
      .upsert({ slug: data.slug, video_id: videoId, name: data.name ?? null, description: data.description ?? null }, { onConflict: "slug" });
    if (error) throw new Error(error.message);
    return { ok: true, videoId };
  });

const SETTING_KEYS = ["title_suffix", "home_show_tools", "home_show_dashboard"] as const;
const SettingInput = z.object({
  key: z.enum(SETTING_KEYS),
  value: z.union([z.string().max(200), z.boolean()]),
});

export const saveSiteSetting = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => SettingInput.parse(d))
  .handler(async ({ data, context }) => {
    await assertAdmin(context as never);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin
      .from("site_settings")
      .upsert({ key: data.key, value: data.value as unknown as never }, { onConflict: "key" });
    if (error) throw new Error(error.message);
    return { ok: true };
  });